import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, ArrowRight, ArrowUpRight, Sparkles } from 'lucide-react';
import { priceLabel } from '../mock';
import { useCatalog } from '../hooks/useCatalog';

const API = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith('/api/') ? `${API}${u}` : u);

const tabs = [
  { id: 'featured', label: 'Pilihan Tim' },
  { id: 'popular', label: 'Terpopuler' },
  { id: 'new', label: 'Terbaru' },
];

export default function ProductShowcase() {
  const { products } = useCatalog();
  const [tab, setTab] = useState('featured');

  const filtered =
    tab === 'popular'
      ? products.filter((p) => ['POPULAR', 'BEST SELLER'].includes(p.badge))
      : tab === 'new'
      ? products.filter((p) => p.badge === 'NEW')
      : products.filter((p) => p.badge);

  if (filtered.length === 0) return null;
  const spotlight = filtered[0];
  const list = filtered.slice(1, 5);
  const grid = filtered.slice(5, 9);

  return (
    <section className="section-pad bg-neutral-50" data-testid="product-showcase-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="text-xs tracking-[0.25em] text-px-red font-semibold mb-3">
              PRODUK UNGGULAN
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-neutral-900">
              Solusi yang sudah teruji.
            </h2>
          </div>
          <div className="flex gap-2 flex-wrap bg-white p-1 rounded-full shadow-sm border border-neutral-200 w-fit">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  tab === t.id
                    ? 'bg-neutral-900 text-white'
                    : 'text-neutral-700 hover:text-neutral-900'
                }`}
                data-testid={`showcase-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Magazine layout: 1 spotlight (7 cols) + 4-row list (5 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mb-5">
          <Link
            to={`/products/${spotlight.id}`}
            data-testid={`showcase-spotlight-${spotlight.id}`}
            className="group relative lg:col-span-7 bg-white rounded-3xl overflow-hidden border border-neutral-200 hover:shadow-xl transition-shadow flex flex-col md:flex-row min-h-[420px]"
          >
            <div className="relative md:w-1/2 bg-gradient-to-br from-neutral-100 to-neutral-50 overflow-hidden flex items-center justify-center">
              {spotlight.badge && (
                <span className="absolute top-5 left-5 z-10 inline-flex items-center gap-1.5 bg-px-red text-white text-[10px] font-bold tracking-wider px-3 py-1.5 rounded-full">
                  <Sparkles className="w-3 h-3" /> {spotlight.badge}
                </span>
              )}
              <img
                src={absUrl(spotlight.image)}
                alt={spotlight.name}
                className="w-full h-full object-contain p-8 md:p-10 aspect-square md:aspect-auto transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="md:w-1/2 p-8 flex flex-col justify-center">
              <p className="text-[10px] tracking-[0.25em] text-neutral-500 font-semibold mb-2">
                {spotlight.sku}
              </p>
              <h3 className="font-display text-2xl lg:text-3xl font-bold text-neutral-900 mb-3 leading-tight">
                {spotlight.name}
              </h3>
              <p className="text-sm text-neutral-600 mb-4 line-clamp-3">
                {spotlight.short}
              </p>
              <p className="text-xs text-neutral-500 mb-6">
                Kapasitas: <strong className="text-neutral-900">{spotlight.capacity}</strong>
              </p>
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-px-red">
                  {priceLabel(spotlight.price)}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs text-neutral-600">
                  <Star className="w-3.5 h-3.5 fill-yellow-500 text-yellow-500" />
                  {spotlight.rating}
                </span>
              </div>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-900 border-b-2 border-px-red pb-0.5 w-fit group-hover:gap-2.5 transition-all">
                Lihat detail <ArrowUpRight className="w-4 h-4" />
              </span>
            </div>
          </Link>

          <div className="lg:col-span-5 flex flex-col gap-3">
            {list.map((p) => (
              <Link
                key={p.id}
                to={`/products/${p.id}`}
                data-testid={`showcase-list-${p.id}`}
                className="group flex items-center gap-4 bg-white rounded-2xl border border-neutral-200 hover:border-neutral-900 hover:shadow-md transition-all p-3 pr-5"
              >
                <div className="relative w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden bg-neutral-100 flex items-center justify-center">
                  {p.badge && (
                    <span className="absolute top-1 left-1 z-10 bg-px-red text-white text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full">
                      {p.badge}
                    </span>
                  )}
                  <img
                    src={absUrl(p.image)}
                    alt={p.name}
                    className="w-full h-full object-contain p-1.5 transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] tracking-wider text-neutral-500 font-semibold">
                    {p.sku}
                  </p>
                  <h4 className="font-display font-bold text-neutral-900 text-sm leading-tight line-clamp-1 mb-1">
                    {p.name}
                  </h4>
                  <p className="text-xs text-neutral-500 line-clamp-1">
                    {p.capacity}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-px-red">
                    {priceLabel(p.price)}
                  </p>
                  <span className="inline-flex items-center gap-1 text-[11px] text-neutral-600 mt-0.5">
                    <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                    {p.rating}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Secondary 4-col grid for the remaining picks */}
        {grid.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {grid.map((p) => (
              <Link
                key={p.id}
                to={`/products/${p.id}`}
                data-testid={`showcase-grid-${p.id}`}
                className="group relative bg-white rounded-2xl overflow-hidden border border-neutral-200 hover:shadow-md hover:-translate-y-1 transition-all flex flex-col"
              >
                {p.badge && (
                  <span className="absolute top-3 left-3 z-10 bg-px-red text-white text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full">
                    {p.badge}
                  </span>
                )}
                <div className="aspect-square bg-neutral-50 overflow-hidden flex items-center justify-center">
                  <img
                    src={absUrl(p.image)}
                    alt={p.name}
                    className="w-full h-full object-contain p-4 transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <p className="text-[10px] tracking-wider text-neutral-500 font-semibold mb-0.5">
                    {p.sku}
                  </p>
                  <h4 className="font-display text-sm font-bold text-neutral-900 mb-1 line-clamp-2 min-h-[2.6em]">
                    {p.name}
                  </h4>
                  <div className="mt-auto flex items-center justify-between">
                    <span className="text-xs font-bold text-px-red">
                      {priceLabel(p.price)}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[11px] text-neutral-600">
                      <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                      {p.rating}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-12 text-center">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-px-red text-white text-sm font-semibold px-8 py-3.5 rounded-full transition-colors"
          >
            Lihat Semua Produk <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
